import React from 'react';
import usePageMetadata from '../utils/usePageMetadata';
import './Privacy.css';

const NON_SENSITIVE_SCOPES = [
  {
    api: 'Google AdSense',
    scope: 'https://www.googleapis.com/auth/adsense.readonly',
    description: 'View your AdSense data'
  }
];

const SENSITIVE_SCOPES = [
  {
    api: 'Google Analytics 4',
    scope: 'https://www.googleapis.com/auth/analytics.readonly',
    description: 'See and download your Google Analytics data'
  },
  {
    api: 'Google Analytics 4',
    scope: 'https://www.googleapis.com/auth/analytics.manage.users.readonly',
    description: 'View Google Analytics user permissions'
  },
  {
    api: 'Google Search Console',
    scope: 'https://www.googleapis.com/auth/webmasters.readonly',
    description: 'View your Google Search Console data'
  }
];

const Privacy = () => {
  usePageMetadata({
    title: 'Privacy Policy | The Ad Shop',
    description:
      'Read how The Ad Shop collects, uses, shares, and protects your data—including Google Analytics, AdSense, and Search Console information—and how to manage or delete your account.',
    canonicalUrl: 'https://theadshop.co/privacy',
    meta: [
      { property: 'og:title', content: 'Privacy Policy | The Ad Shop' },
      {
        property: 'og:description',
        content:
          'Our privacy policy covers Google user data access, usage, sharing, storage safeguards, and data deletion practices for The Ad Shop.',
      },
      { property: 'og:type', content: 'article' },
      { property: 'og:url', content: 'https://theadshop.co/privacy' },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: 'Privacy Policy | The Ad Shop' },
      {
        name: 'twitter:description',
        content:
          'Understand the privacy and data protection practices behind The Ad Shop’s unified analytics platform.',
      },
    ],
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'PrivacyPolicy',
      name: 'The Ad Shop Privacy Policy',
      url: 'https://theadshop.co/privacy',
      description:
        'The Ad Shop explains how publisher data and Google user data are collected, processed, stored, and deleted within the platform.',
      publisher: {
        '@type': 'Organization',
        name: 'The Media Shop, LLC',
        url: 'https://theadshop.co/',
      },
      inLanguage: 'en-US',
      datePublished: '2025-11-09',
      dateModified: '2025-11-09',
    },
  });

  return (
    <div className="privacy-page">
      <div className="privacy-container">
        <div className="privacy-header">
          <h1>Privacy Policy</h1>
          <p className="effective-date">Effective Date: November 9, 2025</p>
        </div>

        <div className="privacy-content">
          <p className="intro">
            The Media Shop, LLC ("we," "us," "our") operates The Ad Shop (the "Service"), available at https://theadshop.co. This Privacy Policy explains how we collect, use, and protect your information when you use our website, products, and services.
          </p>
          <p className="intro">
            By using The Ad Shop, you agree to the terms of this Privacy Policy.
          </p>

          <section className="privacy-section">
            <h2>1. Information We Collect</h2>
            <p>We collect information to operate effectively and provide you with the best experience possible.</p>
            
            <h3>a. Information You Provide</h3>
            <p>When you use The Ad Shop, you may provide:</p>
            <ul>
              <li><strong>Account Information</strong> – name, email address, and password when you sign up.</li>
              <li><strong>Communication Data</strong> – content of messages, support requests, or other correspondence.</li>
              <li><strong>Billing Information</strong> – if you subscribe to paid features (handled securely by third-party payment processors).</li>
            </ul>

            <h3>b. Information Collected Automatically</h3>
            <p>When you visit or use our Service, we automatically collect:</p>
            <ul>
              <li><strong>Usage Data</strong> – pages visited, time on site, referring URLs, and browser type.</li>
              <li><strong>Device Data</strong> – device type, operating system, and screen resolution.</li>
              <li><strong>Cookies & Tracking</strong> – we use cookies and similar technologies to remember preferences and measure site performance.</li>
            </ul>

            <h3>c. Connected Service Data</h3>
            <p>If you choose to connect third-party accounts (e.g., Google Analytics, AdSense, or Search Console) to your The Ad Shop dashboard, we may access:</p>
            <ul>
              <li>Analytics metrics (traffic, sessions, engagement, etc.).</li>
              <li>Monetization data (ad revenue, impressions, etc.).</li>
            </ul>
            <p>This data is pulled securely via API and used solely to display analytics and insights within your dashboard. We never modify or post to your connected accounts.</p>
          </section>

          <section className="privacy-section">
            <h2>2. Google User Data Handling</h2>
            <p>
              When you connect Google products to The Ad Shop, we access Google user data on your behalf strictly to power dashboard features you request. We never access Google data without your explicit consent.
            </p>

            <h3>a. Data Accessed</h3>
            <ul>
              <li><strong>Google Analytics 4</strong> – traffic, engagement, revenue attribution, and device/channel metrics for the properties you authorize.</li>
              <li><strong>Google AdSense</strong> – estimated earnings, impressions, clicks, RPM, and related monetization metrics tied to authorized sites.</li>
              <li><strong>Google Search Console</strong> – search performance metrics such as clicks, impressions, CTR, and average position for approved properties.</li>
              <li><strong>Authentication Tokens</strong> – OAuth tokens required to securely request the data above.</li>
            </ul>

            <h3>b. Data Usage</h3>
            <p>We use Google user data to:</p>
            <ul>
              <li>Populate analytics dashboards and reports for your connected sites.</li>
              <li>Generate insights, compliance checks, and optimization recommendations visible only within your account.</li>
              <li>Diagnose ingestion issues, fraud, or security events related to your integrations.</li>
            </ul>

            <h3>c. Data Sharing</h3>
            <p>
              We do not sell or share Google user data with third parties. Limited access is provided only to subprocessors that deliver our service (e.g., cloud hosting, database, and error monitoring providers), all of whom are bound by confidentiality and data protection obligations. We never use Google user data for advertising or marketing our own products.
            </p>

            <h3>d. Data Storage & Protection</h3>
            <ul>
              <li>OAuth refresh tokens are encrypted at rest using industry-standard AES-256 encryption.</li>
              <li>Access is restricted to least-privileged service accounts and audited administrators.</li>
              <li>Data is stored in the United States using hardened infrastructure with network segmentation, logging, and intrusion detection.</li>
              <li>We regularly review integrations to ensure compliance with Google API Services User Data Policy.</li>
            </ul>

            <h3>e. Data Retention & Deletion</h3>
            <ul>
              <li>We retain Google user data only while your account is active and the integration remains connected.</li>
              <li>Disconnecting a Google integration revokes tokens and stops new data pulls; cached records age out under our retention schedule below.</li>
              <li>You may delete your account at any time from Account Settings; we then purge Google user data and revoke tokens within 30 days.</li>
              <li>You can also email <a href="mailto:support@theadshop.co">support@theadshop.co</a> to request expedited deletion.</li>
            </ul>
          </section>

          <section className="privacy-section scope-section">
            <h2>Google OAuth Scopes We Request</h2>
            <p>
              We only request the Google OAuth scopes required to connect your analytics and monetization data.
              They are grouped by Google&rsquo;s sensitivity classification so you know what requires additional approval.
            </p>

            <div className="scope-panels">
              <div className="scope-panel">
                <div className="scope-panel-heading">
                  <p className="scope-panel-title">Your non-sensitive scopes</p>
                </div>
                <div className="scope-table-wrapper">
                  <table className="scope-table">
                    <thead>
                      <tr>
                        <th>API</th>
                        <th>Scope</th>
                        <th>User-facing description</th>
                      </tr>
                    </thead>
                    <tbody>
                      {NON_SENSITIVE_SCOPES.map((item) => (
                        <tr key={item.scope}>
                          <td>{item.api}</td>
                          <td><code>{item.scope}</code></td>
                          <td>{item.description}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="scope-panel">
                <div className="scope-panel-heading">
                  <p className="scope-panel-title">Your sensitive scopes</p>
                  <p className="scope-panel-note">Approval required.</p>
                </div>
                <div className="scope-table-wrapper">
                  <table className="scope-table">
                    <thead>
                      <tr>
                        <th>API</th>
                        <th>Scope</th>
                        <th>User-facing description</th>
                      </tr>
                    </thead>
                    <tbody>
                      {SENSITIVE_SCOPES.map((item) => (
                        <tr key={item.scope}>
                          <td>{item.api}</td>
                          <td><code>{item.scope}</code></td>
                          <td>{item.description}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </section>

          <section className="privacy-section">
            <h2>3. How We Use Information</h2>
            <p>We use collected data to:</p>
            <ul>
              <li>Provide, maintain, and improve The Ad Shop functionality.</li>
              <li>Personalize your dashboard and recommendations.</li>
              <li>Respond to support inquiries and feedback.</li>
              <li>Communicate about updates, security alerts, or administrative matters.</li>
              <li>Analyze aggregated trends and product performance.</li>
            </ul>
            <p>We do not sell or share your personal data with third parties for marketing purposes.</p>
          </section>

          <section className="privacy-section">
            <h2>4. Data Sharing and Disclosure</h2>
            <p>We may share limited information in the following cases:</p>
            <ul>
              <li><strong>Service Providers</strong> – trusted vendors who help us operate our business (e.g., hosting, email delivery, analytics).</li>
              <li><strong>Legal Requirements</strong> – if required by law or to protect our rights and users.</li>
              <li><strong>Business Transfers</strong> – if The Media Shop, LLC is involved in a merger, acquisition, or sale of assets, your information may be transferred as part of that transaction.</li>
            </ul>
            <p>All service providers are bound by confidentiality and data protection obligations. We never grant them permission to use Google user data for their own purposes.</p>
          </section>

          <section className="privacy-section">
            <h2>5. Cookies and Tracking</h2>
            <p>We use cookies to:</p>
            <ul>
              <li>Keep you signed in.</li>
              <li>Remember preferences and settings.</li>
              <li>Measure traffic and usage patterns.</li>
            </ul>
            <p>You can control cookies through your browser settings. Disabling cookies may limit functionality.</p>
          </section>

          <section className="privacy-section">
            <h2>6. Data Retention</h2>
            <p>
              We retain personal and product usage data only while needed to operate the Service, comply with our legal obligations, or resolve disputes. Aggregated analytics data ages out under rolling windows (typically 30–180 days depending on data type). Once you delete your account or request deletion, we remove or anonymize associated records within 30 days unless a longer retention period is required by law.
            </p>
          </section>

          <section className="privacy-section">
            <h2>7. Data Security</h2>
            <p>We take appropriate technical and organizational measures to protect your data from unauthorized access, alteration, or loss, including:</p>
            <ul>
              <li>Encrypted connections (HTTPS).</li>
              <li>Encryption at rest for OAuth tokens and sensitive identifiers.</li>
              <li>Limited employee access to production systems.</li>
              <li>Secure API authentication for connected accounts and automatic token revocation on suspicious activity.</li>
            </ul>
            <p>However, no system is completely secure, and we cannot guarantee absolute security.</p>
          </section>

          <section className="privacy-section">
            <h2>8. Your Rights</h2>
            <p>Depending on your location, you may have the following rights:</p>
            <ul>
              <li><strong>Access</strong> – request a copy of the personal data we hold about you.</li>
              <li><strong>Correction</strong> – request correction of inaccurate or incomplete data.</li>
              <li><strong>Deletion</strong> – delete your account (and connected Google data) from Account Settings or request deletion via email.</li>
              <li><strong>Portability</strong> – request transfer of your data to another provider.</li>
              <li><strong>Opt-Out</strong> – unsubscribe from non-essential communications or revoke connected app permissions.</li>
            </ul>
            <p>You can manage account deletion directly in the application under Account Settings &gt; Privacy Controls. For any other requests, contact us at <a href="mailto:support@theadshop.co">support@theadshop.co</a>.</p>
          </section>

          <section className="privacy-section">
            <h2>9. International Data Transfers</h2>
            <p>Your information may be processed and stored in the United States or other locations where we operate. We comply with applicable data-transfer laws to protect your information.</p>
          </section>

          <section className="privacy-section">
            <h2>10. Children's Privacy</h2>
            <p>Our Service is not directed to children under 13, and we do not knowingly collect personal information from them.</p>
          </section>

          <section className="privacy-section">
            <h2>11. Changes to This Policy</h2>
            <p>We may update this Privacy Policy from time to time. The latest version will always be posted on this page with a revised effective date.</p>
          </section>

          <section className="privacy-section">
            <h2>12. Contact Us</h2>
            <p>For questions, concerns, or requests regarding this Privacy Policy, please contact:</p>
            <div className="contact-info">
              <p><strong>The Media Shop, LLC</strong></p>
              <p>Email: <a href="mailto:support@theadshop.co">support@theadshop.co</a></p>
              <p>Austin, Texas, USA</p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default Privacy;

